/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useEffect } from 'react';
import { colors, borderRadius, shadows, typography, transitions } from '../../styles/theme';
import { CloseIcon, ChevronLeftIcon, ChevronRightIcon } from '../common';

const modalOverlayStyles = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  backdrop-filter: blur(4px);
`;

const modalContentStyles = css`
  background: white;
  border-radius: ${borderRadius['2xl']};
  max-width: 860px;
  width: 100%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
`;

const modalHeaderStyles = css`
  background: linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[800]} 100%);
  color: white;
  padding: 1.25rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;

  @media (min-width: 768px) {
    padding: 1.5rem 2rem;
  }

  h3 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize.xl};
    font-weight: ${typography.fontWeight.bold};
    margin: 0;
  }

  button {
    background: rgba(255, 255, 255, 0.2);
    border: none;
    color: white;
    width: 36px;
    height: 36px;
    border-radius: ${borderRadius.full};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all ${transitions.fast};

    svg {
      width: 18px;
      height: 18px;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

const modalBodyStyles = css`
  padding: 1.5rem;
  overflow-y: auto;

  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const sliderContentStyles = css`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const sliderImageStyles = css`
  flex-shrink: 0;
  display: flex;
  justify-content: center;

  img {
    width: auto;
    height: min(52vh, 460px);
    border-radius: ${borderRadius.xl};
    box-shadow: ${shadows.lg};
  }
`;

const sliderInfoStyles = css`
  flex: 1;

  .step-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: ${colors.primary[100]};
    color: ${colors.primary[700]};
    padding: 0.5rem 1rem;
    border-radius: ${borderRadius.full};
    font-weight: ${typography.fontWeight.semibold};
    font-size: ${typography.fontSize.sm};
    margin-bottom: 1rem;
  }

  h4 {
    font-family: ${typography.fontFamily.heading};
    font-size: ${typography.fontSize['2xl']};
    font-weight: ${typography.fontWeight.bold};
    color: ${colors.text.primary};
    margin-bottom: 1rem;
  }

  p {
    color: ${colors.text.secondary};
    line-height: 1.7;
    font-size: ${typography.fontSize.base};
  }
`;

const sliderNavStyles = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid ${colors.neutral[200]};
`;

const sliderDotsStyles = css`
  display: flex;
  gap: 0.5rem;
`;

const sliderDotStyles = css`
  width: 10px;
  height: 10px;
  border-radius: ${borderRadius.full};
  background: ${colors.neutral[300]};
  border: none;
  cursor: pointer;
  transition: all ${transitions.fast};

  &.active {
    background: ${colors.primary[600]};
    width: 24px;
  }

  &:hover {
    background: ${colors.primary[400]};
  }
`;

const sliderArrowStyles = css`
  display: flex;
  gap: 0.5rem;

  button {
    width: 40px;
    height: 40px;
    border-radius: ${borderRadius.full};
    border: 2px solid ${colors.neutral[300]};
    background: white;
    cursor: pointer;
    color: ${colors.text.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all ${transitions.fast};

    svg {
      width: 18px;
      height: 18px;
    }

    &:hover:not(:disabled) {
      border-color: ${colors.primary[600]};
      color: ${colors.primary[700]};
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
`;

const appSteps = [
  {
    id: 1,
    title: 'Uygulamayı Açın',
    description: 'YolSepetiGO uygulamasını açın ve splash ekranı ile karşılaşın. Uygulama hızlıca yüklenecek ve sizi ana sayfaya yönlendirecek.',
    image: '/images/promo/1.png',
  },
  {
    id: 2,
    title: 'Ana Sayfayı İnceleyin',
    description: 'Ana sayfada 4 ana hizmetimizi göreceksiniz: Çekici, Vinç, Yol Yardım ve Nakliye. İstediğiniz hizmete tıklayın.',
    image: '/images/promo/2.png',
  },
  {
    id: 3,
    title: 'Konum Bilgisi Girin',
    description: 'Alış ve teslim noktalarını girin. Haritadan seçebilir veya adres yazabilirsiniz. GPS ile otomatik konum algılama da mevcuttur.',
    image: '/images/promo/3.png',
  },
  {
    id: 4,
    title: 'Araç ve Detayları Belirtin',
    description: 'Araç tipinizi seçin ve gerekli detayları girin. Araç resmi ekleyebilir, özel notlar yazabilirsiniz.',
    image: '/images/promo/4.png',
  },
  {
    id: 5,
    title: 'Fiyat Teklifini Görün',
    description: 'Girdiğiniz bilgilere göre anlık fiyat hesaplanır. Şeffaf fiyatlandırma ile sürpriz maliyetler yok.',
    image: '/images/promo/5.png',
  },
  {
    id: 6,
    title: 'Ödeme Yapın',
    description: 'VISA, MasterCard veya Troy ile güvenli ödeme yapın. Tüm ödemeler SSL ile şifrelenir.',
    image: '/images/promo/6.png',
  },
  {
    id: 7,
    title: 'Sürücüyü Takip Edin',
    description: 'Eşleşen sürücünüzü haritada canlı takip edin. Tahmini varış süresi ve sürücü bilgilerini görün.',
    image: '/images/promo/7.png',
  },
];

interface AppModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
}

export default function AppModal({ isOpen, onClose, currentStep, setCurrentStep }: AppModalProps) {
  const nextStep = () => {
    if (currentStep < appSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && currentStep < appSteps.length - 1) setCurrentStep(currentStep + 1);
      if (e.key === 'ArrowLeft' && currentStep > 0) setCurrentStep(currentStep - 1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentStep, onClose, setCurrentStep]);

  if (!isOpen) return null;

  return (
    <div css={modalOverlayStyles} onClick={onClose}>
      <div
        css={modalContentStyles}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-modal-title"
      >
        <div css={modalHeaderStyles}>
          <h3 id="app-modal-title">Mobil Uygulama Rehberi</h3>
          <button onClick={onClose} aria-label="Kapat" autoFocus>
            <CloseIcon />
          </button>
        </div>
        <div css={modalBodyStyles}>
          <div css={sliderContentStyles}>
            <div css={sliderImageStyles}>
              <img
                src={appSteps[currentStep].image}
                alt={appSteps[currentStep].title}
              />
            </div>
            <div css={sliderInfoStyles}>
              <div className="step-badge">
                Adım {appSteps[currentStep].id} / {appSteps.length}
              </div>
              <h4>{appSteps[currentStep].title}</h4>
              <p>{appSteps[currentStep].description}</p>
            </div>
          </div>
          <div css={sliderNavStyles}>
            <div css={sliderDotsStyles}>
              {appSteps.map((_, index) => (
                <button
                  key={index}
                  css={sliderDotStyles}
                  className={index === currentStep ? 'active' : ''}
                  onClick={() => setCurrentStep(index)}
                  aria-label={`Adım ${index + 1}`}
                />
              ))}
            </div>
            <div css={sliderArrowStyles}>
              <button onClick={prevStep} disabled={currentStep === 0} aria-label="Önceki adım">
                <ChevronLeftIcon />
              </button>
              <button
                onClick={nextStep}
                disabled={currentStep === appSteps.length - 1}
                aria-label="Sonraki adım"
              >
                <ChevronRightIcon />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
